import React, { FC, useMemo, useState } from "react";
import Modal from "@/components/Modal/index";
import { useAppTheme } from "@/utils/theme";
import Box from "@mui/material/Box";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import Button from "@/components/Button";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { ErrorResponse } from "@/domain/error.interface";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import { useUserStore } from "@/hooks/useUserStore";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import { Typography } from "@mui/material";
import addValidationSchema, { AddRoleFormData } from "./validation.schema";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { a11yProps } from "@/utils/a11yProps";
import TabPanel from "@/components/TabPanel";
import { TabRoleGeneral } from "./TabRoleGeneral";
import { TabRoleActions } from "./TabRoleActions";
import { useCountrySetting } from "@/hooks/useSetting";
import { RoleItem } from "@/domain/role.interface";
import { useUpdateRole } from "@/hooks/management/useRoles.manage";
import { RoleStatusEnum, RoleTypeEnum } from "@/domain/role.enum";

type RoleUpdateModalProps = {
  open: boolean;
  role: RoleItem;
  onClose: () => void;
};

const RoleUpdateModal: FC<RoleUpdateModalProps> = ({ open, role, onClose }) => {
  const { t } = useTranslation("roles");
  const { t: tCommon } = useTranslation("common");
  const { userInfo } = useUserStore();
  const { showSnackBar } = useFeedBack();
  const theme = useAppTheme();

  const createAuditLog = useCreateAuditlog();
  const updateRole = useUpdateRole();

  const [selectedTab, setSelectedTab] = useState(0);

  const defaultValues = useMemo(() => {
    return {
      name: role.name,
      status: role.status,
      type: role.type,
      actions: role.actions,
    };
  }, [role]);

  const methods = useForm<AddRoleFormData>({
    resolver: yupResolver(addValidationSchema(tCommon)),
    mode: "all",
    defaultValues,
  });

  const handleTabChange = async (
    event: React.SyntheticEvent,
    newValue: number,
  ) => {
    setSelectedTab(newValue);
  };

  const onSubmit = async (data: AddRoleFormData) => {
    const payload = {
      name: data.name as string,
      actions: data.actions as string[],
      status: data.status as RoleStatusEnum,
      type: data.type as RoleTypeEnum,
      updatedBy: userInfo?._id,
    };

    await updateRole.mutateAsync(
      {
        id: role._id,
        data: payload,
      },
      {
        onSuccess: () => {
          onClose();
          showSnackBar(t("messages.update-success"), "success");

          createAuditLog.mutate({
            appType: "WEB_BACK_OFFICE",
            module: MODULE_NAME.ROLES,
            option: OPTIONS.ROLES_EDIT,
            actionCode: ACTION_CODE.WEB_ROLES_EDIT,
            action: "When editing a role",
            detail: `Edited role ${data.name}`,
            transactionDate: new Date(),
            accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
            createdBy: userInfo?._id || "",
            name: `${userInfo?.firstName} ${userInfo?.lastName}`,
          });
        },
        onError: (error: ErrorResponse) => {
          showSnackBar(t("messages.update-error"), "error");
        },
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={false}
      title={t("modals.title-edit")}
      theme={theme}
      size={{
        width: "100%",
        minHeight: 440,
        maxHeight: 720,
        maxWidth: 860,
      }}
      content={
        <FormProvider {...methods}>
          <Box sx={{ width: "100%", minHeight: 380 }}>
            <Box
              sx={{
                padding: "20px 0 0",
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
              }}
            >
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                aria-label="invite user tabs"
              >
                <Tab label={t("tabs.create-role")} {...a11yProps(0)} />
                <Tab label={t("tabs.assign-actions")} {...a11yProps(1)} />
              </Tabs>
            </Box>
            <TabPanel value={selectedTab} index={0}>
              <TabRoleGeneral />
            </TabPanel>
            <TabPanel value={selectedTab} index={1}>
              <TabRoleActions />
            </TabPanel>
          </Box>
        </FormProvider>
      }
      actions={
        <Box
          sx={{
            display: "flex",
            gap: "10px",
          }}
        >
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={false}
            label={t("buttons.cancel")}
          />
          {role?.type !== null && (
            <Button
              variantType="save"
              onClick={async () => {
                methods.handleSubmit(onSubmit)();
              }}
              disabled={updateRole.isPending}
              isLoading={updateRole.isPending}
              label={t("buttons.save")}
            />
          )}
        </Box>
      }
    />
  );
};

export default RoleUpdateModal;
